import {
  ApiError,
  type Installation,
  type InstallationSettings,
  type PaginatedReviews,
  type RepoSettings,
  type ReviewDetail,
  type ReviewListParams,
  type Usage,
  type User,
} from '../types/api';

// When VITE_API_BASE_URL is set (e.g. ngrok), all requests go directly to that origin.
// When unset, requests use relative /api paths (proxied by Vite dev server or Nginx).
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

// ── Low-level fetch wrapper ───────────────────────────────────────────────────

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Prefer Authorization header over cookies — works across different origins
  // (e.g. API on api.domain.com, dashboard on domain.com) without third-party
  // cookie restrictions. Token is stored in localStorage by AuthCallback.tsx
  // after the GitHub OAuth redirect.
  const token = localStorage.getItem('session_token');

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // Bypass ngrok's browser-warning interstitial page (free tier).
      'ngrok-skip-browser-warning': 'true',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });

  if (res.status === 401) {
    // Token expired or invalid — always clear it
    localStorage.removeItem('session_token');
    if (window.location.pathname !== '/login') {
      // Freeze the promise chain while the browser navigates away
      window.location.href = '/login';
      return new Promise(() => {});
    }
    // Already on login page — throw so the caller's catch block can handle it
    // gracefully (sets user=null, loading=false) without hanging indefinitely.
    throw new ApiError('Not authenticated', 'UNAUTHENTICATED', 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { message: res.statusText, code: 'UNKNOWN' } }));
    const err = body?.error ?? { message: res.statusText, code: 'UNKNOWN' };
    throw new ApiError(err.message, err.code, res.status);
  }

  return res.json() as Promise<T>;
}

// ── Query string helper ───────────────────────────────────────────────────────

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  return new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

// ── Typed API surface ─────────────────────────────────────────────────────────

export const api = {
  auth: {
    me: () =>
      request<{ data: User }>('/auth/me'),

    logout: () =>
      request<{ data: { message: string } }>('/auth/logout', { method: 'POST' }),
  },

  installations: {
    list: () =>
      request<{ data: Installation[] }>('/installations'),

    get: (id: string) =>
      request<{ data: Installation }>(`/installations/${id}`),
  },

  reviews: {
    list: (params: ReviewListParams, signal?: AbortSignal) =>
      request<{ data: PaginatedReviews }>(`/reviews?${toQueryString(params as unknown as Record<string, string | number | boolean | undefined>)}`, { signal }),

    get: (id: string) =>
      request<{ data: ReviewDetail }>(`/reviews/${id}`),

    triggerFix: (id: string) =>
      request<{ data: { fixStatus: string } }>(`/reviews/${id}/fix`, { method: 'POST' }),
  },

  settings: {
    getInstallation: (id: string) =>
      request<{ data: InstallationSettings }>(`/settings/installations/${id}`),

    updateInstallation: (id: string, data: Partial<InstallationSettings>) =>
      request<{ data: InstallationSettings }>(`/settings/installations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    getRepo: (id: string) =>
      request<{ data: RepoSettings }>(`/settings/repos/${id}`),

    updateRepo: (id: string, data: Partial<RepoSettings>) =>
      request<{ data: RepoSettings }>(`/settings/repos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  usage: {
    getCurrent: (installationId: string) =>
      request<{ data: Usage }>(`/usage?installationId=${installationId}`),

    getHistory: (installationId: string) =>
      request<{ data: Usage[] }>(`/usage/history?installationId=${installationId}`),
  },
};
